import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { relativeChange, SalesSummary } from "./salesSummary.ts";
import {
  formatChange,
  formatCount,
  formatEuros,
  formatPercent,
  formatSignedCount,
  formatSignedEuros,
} from "./salesFormat.ts";
import { LoadingBlock } from "./StatisticsCard.tsx";
import { Sparkline, SparklinePoint } from "./Sparkline.tsx";
import { InfoHint } from "./InfoHint.tsx";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";

type SummaryBucket = { label: string; summary: SalesSummary };

type TileDefinition = {
  label: string;
  info: string;
  valueOf: (summary: SalesSummary) => number;
  isCount?: boolean;
  isGrowthBad?: boolean;
  noteOf?: (summary: SalesSummary) => string | undefined;
};

const TILES: TileDefinition[] = [
  {
    label: "Venduto",
    info: SALES_DEFINITIONS.sold,
    valueOf: (summary) => summary.soldInCents,
    noteOf: () => "Ordini conclusi, al netto dei coupon",
  },
  {
    label: "Venduto netto",
    info: SALES_DEFINITIONS.netSold,
    valueOf: (summary) => summary.netSoldInCents,
    noteOf: () => "Al netto anche dei rimborsi",
  },
  {
    label: "Incassato",
    info: SALES_DEFINITIONS.collected,
    valueOf: (summary) => summary.collectedInCents,
    noteOf: (summary) =>
      summary.collectedFromSettlementsInCents > 0
        ? `Di cui ${formatEuros(summary.collectedFromSettlementsInCents)} da saldi e integrazioni`
        : "Pagamenti ricevuti nel periodo",
  },
  {
    label: "Ordini conclusi",
    info: SALES_DEFINITIONS.orders,
    valueOf: (summary) => summary.orderCount,
    isCount: true,
  },
  {
    label: "Valore medio ordine",
    info: SALES_DEFINITIONS.averageOrder,
    valueOf: (summary) => summary.averageOrderInCents,
  },
  {
    label: "Rimborsi",
    info: SALES_DEFINITIONS.refunds,
    valueOf: (summary) => summary.refundedInCents,
    isGrowthBad: true,
    noteOf: (summary) =>
      summary.soldInCents > 0
        ? `${formatPercent(summary.refundedInCents / summary.soldInCents)} del venduto`
        : undefined,
  },
];

function ChangeVsPrevious({
  current,
  previous,
  isCount,
  isGrowthBad,
  previousPeriodLabel,
  comparisonShortLabel,
}: {
  current: number;
  previous: number;
  isCount?: boolean;
  isGrowthBad?: boolean;
  previousPeriodLabel: string;
  comparisonShortLabel: string;
}) {
  const formatValue = isCount ? formatCount : formatEuros;
  const formatDifference = isCount ? formatSignedCount : formatSignedEuros;
  const comparison = `${previousPeriodLabel}: ${formatValue(previous)}`;
  const change = relativeChange(current, previous);
  if (change === null || current === previous)
    return (
      <span className="text-xs text-gray-400" title={comparison}>
        {change === null ? "Nessun valore da confrontare" : "Invariato"}
      </span>
    );
  const isGood = change > 0 !== !!isGrowthBad;
  const Arrow = change > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      title={comparison}
      className={`flex flex-wrap items-center gap-x-1 text-xs font-medium ${isGood ? "text-emerald-700" : "text-red-700"}`}
    >
      <Arrow className="w-3.5 h-3.5" aria-hidden />
      {formatChange(change)}
      <span className="font-normal text-gray-400">
        {formatDifference(current - previous)} {comparisonShortLabel}
      </span>
    </span>
  );
}

export function SalesSummaryTiles({
  current,
  previous,
  previousPeriodLabel,
  comparisonShortLabel,
  buckets,
  isLoading,
}: {
  current: SalesSummary;
  previous: SalesSummary | null;
  previousPeriodLabel: string;
  comparisonShortLabel: string;
  buckets: SummaryBucket[];
  isLoading: boolean;
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      {TILES.map((tile) => {
        const formatValue = tile.isCount ? formatCount : formatEuros;
        const note = tile.noteOf?.(current);
        const points: SparklinePoint[] = buckets.map((bucket) => ({
          label: bucket.label,
          value: tile.valueOf(bucket.summary),
        }));
        return (
          <div
            key={tile.label}
            className="flex flex-col gap-1 min-w-0 rounded-lg bg-white p-4 shadow-md shadow-slate-200"
          >
            <span className="flex items-center gap-1 text-xs text-gray-500">
              {tile.label}
              <InfoHint text={tile.info} />
            </span>
            {isLoading ? (
              <LoadingBlock className="h-8 w-28" />
            ) : (
              <>
                <span className="text-2xl font-bold text-gray-800 truncate">
                  {formatValue(tile.valueOf(current))}
                </span>
                {note && <span className="text-xs text-gray-500">{note}</span>}
                {previous && (
                  <ChangeVsPrevious
                    current={tile.valueOf(current)}
                    previous={tile.valueOf(previous)}
                    isCount={tile.isCount}
                    isGrowthBad={tile.isGrowthBad}
                    previousPeriodLabel={previousPeriodLabel}
                    comparisonShortLabel={comparisonShortLabel}
                  />
                )}
                <div className="mt-auto pt-1">
                  <Sparkline points={points} formatValue={formatValue} />
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
